import React, { Suspense, useEffect, Component, ErrorInfo, ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';
import { NotificationProvider } from './context/NotificationContext';
import { CookieConsentProvider, useCookieConsent } from './context/CookieConsentContext';
import { GlobalStyles } from './styles/GlobalStyles';
import HeaderLuxury from './components/HeaderLuxury';
import FooterLuxury from './components/FooterLuxury';
import ScrollToTop from './components/ScrollToTop';
import { trackPageView } from './utils/visitorTracking';
import { trackPageView as fbTrackPageView } from './utils/metaPixel';

import CookieConsent from './components/CookieConsent';
import CartNotificationWrapper from './components/CartNotificationWrapper';
import PerformanceOptimizer from './components/PerformanceOptimizer';
import AdminRoute from './components/AdminRoute';
import ThemeProvider from './admin/context/ThemeContext';

// Loading fallback for lazy-loaded routes
const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    Loading...
  </div>
);

// Lazy-loaded page components
const Home = React.lazy(() => import('./pages/Home'));
const About = React.lazy(() => import('./pages/About'));
const Signature = React.lazy(() => import('./pages/Signature'));
const Contact = React.lazy(() => import('./pages/Contact'));
const CategoryPage = React.lazy(() => import('./pages/CategoryPage'));
const ProductDetailMinimal = React.lazy(() => import('./pages/ProductDetailMinimal'));
const Blog = React.lazy(() => import('./pages/Blog'));
const Sustainability = React.lazy(() => import('./pages/Sustainability'));
const Cart = React.lazy(() => import('./pages/Cart'));
const GuestCheckoutMultiStep = React.lazy(() => import('./pages/GuestCheckoutMultiStep'));
const OrderConfirmation = React.lazy(() => import('./pages/OrderConfirmation'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));
const Profile = React.lazy(() => import('./pages/Profile'));
const Orders = React.lazy(() => import('./pages/Orders'));
const OrderDetail = React.lazy(() => import('./pages/OrderDetail'));
const OrderSuccess = React.lazy(() => import('./pages/OrderSuccess'));
const TrackOrder = React.lazy(() => import('./pages/TrackOrder'));
const OrderDetailGuest = React.lazy(() => import('./pages/OrderDetailGuest'));
const AdminLogin = React.lazy(() => import('./pages/AdminLogin'));
const PaymentCallback = React.lazy(() => import('./pages/PaymentCallback'));
const PaymentSuccess = React.lazy(() => import('./pages/PaymentSuccess'));
const PaymentError = React.lazy(() => import('./pages/PaymentError'));
const PaymentProcessing = React.lazy(() => import('./pages/PaymentProcessing'));
const PaymentFailed = React.lazy(() => import('./pages/PaymentFailed'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const Returns = React.lazy(() => import('./pages/Returns'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfUse = React.lazy(() => import('./pages/TermsOfUse'));
const Shipping = React.lazy(() => import('./pages/Shipping'));

// Lazy-loaded admin components
const AdminLayout = React.lazy(() => import('./admin/components/AdminLayout'));
const AnalyticsEnhanced = React.lazy(() => import('./pages/admin/AnalyticsEnhanced'));
const CustomerSegmentation = React.lazy(() => import('./pages/admin/CustomerSegmentationEnhanced'));
const AdvancedAnalytics = React.lazy(() => import('./pages/admin/AdvancedAnalytics'));
const ProductManagement = React.lazy(() => import('./pages/admin/ProductManagement'));
const ProductFormPage = React.lazy(() => import('./admin/components/product-form/ProductFormPage'));
const AdminOrders = React.lazy(() => import('./pages/admin/Orders'));
const OrderDetailAdmin = React.lazy(() => import('./pages/admin/OrderDetailAdmin'));
const CampaignManagement = React.lazy(() => import('./pages/admin/CampaignManagement'));
const CampaignDetail = React.lazy(() => import('./pages/admin/CampaignDetail'));
const CouponManagement = React.lazy(() => import('./pages/admin/CouponManagement'));
const CustomerManagement = React.lazy(() => import('./pages/admin/CustomerManagement'));
const CustomerDetail = React.lazy(() => import('./pages/admin/CustomerDetail'));
const CustomerSegmentList = React.lazy(() => import('./pages/admin/CustomerSegmentList'));
const ShippingManagement = React.lazy(() => import('./pages/admin/ShippingManagement'));
const Settings = React.lazy(() => import('./pages/admin/Settings'));
const HomepageEditor = React.lazy(() => import('./pages/admin/HomepageEditor'));
const NavigationEditor = React.lazy(() => import('./pages/admin/NavigationEditor'));
const AnnouncementEditor = React.lazy(() => import('./pages/admin/AnnouncementEditor'));
const AccessoryManagement = React.lazy(() => import('./pages/admin/AccessoryManagement'));
const EmailTemplates = React.lazy(() => import('./pages/admin/EmailTemplates'));
const EmailTemplateEdit = React.lazy(() => import('./pages/admin/EmailTemplateEdit'));
const EmailTemplatePreview = React.lazy(() => import('./pages/admin/EmailTemplatePreview'));
const ProductPreview = React.lazy(() => import('./pages/admin/ProductPreview'));
const CategoryForm = React.lazy(() => import('./pages/admin/CategoryForm'));
const RefundManagement = React.lazy(() => import('./pages/admin/RefundManagement'));

// Error Boundary
interface ErrorBoundaryProps {
  children: ReactNode;
  resetKey?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('App error:', error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'Inter, sans-serif', textAlign: 'center', padding: '2rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 400, marginBottom: '1rem' }}>Something went wrong</h1>
          <p style={{ color: '#666', marginBottom: '2rem' }}>An unexpected error occurred. Please refresh the page.</p>
          <button onClick={() => window.location.reload()} style={{ padding: '12px 32px', background: '#1a1a1a', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.875rem', letterSpacing: '0.05em' }}>
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Wrapper that provides location-based resetKey to ErrorBoundary
const LocationAwareErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
};

// Protected Route wrapper for auth-required pages
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { state } = useAuth();
  const location = useLocation();
  if (state.isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>Loading...</div>;
  }
  if (!state.isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
};

// Layout component with conditional footer
const MainAppLayout = () => {
  const location = useLocation();
  const { state: authState } = useAuth();
  const { consent } = useCookieConsent();
  const isProductDetailPage = location.pathname.startsWith('/product/') ||
                               location.pathname.startsWith('/product-minimal/') ||
                               location.pathname.startsWith('/product-luxury/');
  const isCheckoutPage = location.pathname.startsWith('/checkout') ||
                         location.pathname.startsWith('/guest-checkout');
  const isOrderConfirmationPage = location.pathname.startsWith('/order-confirmation');
  const isTrackOrderPage = location.pathname === '/track-order';
  const isSignaturePage = location.pathname === '/craft-behind';

  // Track page views on route change
  useEffect(() => {
    // Only track if not admin route
    if (!location.pathname.startsWith('/admin')) {
      if (consent?.analytics) {
        trackPageView(location.pathname);
      }
      if (consent?.marketing) {
        fbTrackPageView();
      }
    }
  }, [location.pathname, consent]);

  // CSS variable is now managed by HeaderLuxury component

  return (
    <div className="App">
      <HeaderLuxury />
      <main className={isProductDetailPage ? 'no-padding' : ''}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/craft-behind" element={<Signature />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/product/:slug" element={<ProductDetailMinimal />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/sustainability" element={<Sustainability />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Navigate to="/guest-checkout" replace />} />
          <Route path="/guest-checkout" element={<GuestCheckoutMultiStep />} />
          <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
          <Route path="/order-success/:orderId" element={<OrderSuccess />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
          <Route path="/order/:id" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
          <Route path="/track-order" element={<TrackOrder />} />
          <Route path="/order-detail" element={<OrderDetailGuest />} />
          <Route path="/payment/callback" element={<PaymentCallback />} />
          <Route path="/payment/processing" element={<PaymentProcessing />} />
          <Route path="/payment/failed" element={<PaymentFailed />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-error" element={<PaymentError />} />
          <Route path="/returns" element={<Returns />} />
          <Route path="/shipping" element={<Shipping />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          {/* Dynamic category pages - WARNING: This catches ALL unmatched single-segment URLs
              before the 404 route. CategoryPage must handle invalid slugs gracefully
              by showing a 404/NotFound view when the slug doesn't match any category. */}
          <Route path="/:slug" element={<CategoryPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!isProductDetailPage && !isCheckoutPage && !isOrderConfirmationPage && !isTrackOrderPage && !isSignaturePage && <FooterLuxury />}
      <CartNotificationWrapper />
      <CookieConsent />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
    <CookieConsentProvider>
    <LanguageProvider>
      <AuthProvider>
        <CartProvider>
          <NotificationProvider>
            <Router>
          <LocationAwareErrorBoundary>
          <GlobalStyles />
          <ScrollToTop />
          <PerformanceOptimizer>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* Protected Admin Panel Routes - No Header/Footer */}
              <Route path="/admin/*" element={
                <AdminRoute>
                  <ThemeProvider>
                    <ErrorBoundary>
                    <div className="admin-app" style={{ fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                      <Routes>
                        <Route path="/" element={<AdminLayout />}>
                          <Route index element={<AnalyticsEnhanced />} />
                          <Route path="dashboard" element={<AnalyticsEnhanced />} />
                          <Route path="analytics" element={<AnalyticsEnhanced />} />
                          <Route path="orders" element={<AdminOrders />} />
                          <Route path="orders/:orderId" element={<OrderDetailAdmin />} />
                          <Route path="refunds" element={<RefundManagement />} />
                          <Route path="advanced-analytics" element={<AdvancedAnalytics />} />
                          <Route path="customers-old" element={<CustomerSegmentation />} />
                          <Route path="customers/:customerId" element={<CustomerDetail />} />
                          <Route path="segments/:segmentId" element={<CustomerSegmentList />} />
                          <Route path="products" element={<ProductManagement />} />
                          <Route path="products/new" element={<ProductFormPage />} />
                          <Route path="products/:id/edit" element={<ProductFormPage />} />
                          <Route path="products/:productId/preview" element={<ProductPreview />} />
                          <Route path="campaigns" element={<CampaignManagement />} />
                          <Route path="campaigns/:campaignId" element={<CampaignDetail />} />
                          <Route path="coupons" element={<CouponManagement />} />
                          <Route path="email-templates" element={<EmailTemplates />} />
                          <Route path="email-templates/:templateId/edit" element={<EmailTemplateEdit />} />
                          <Route path="email-templates/:templateId/preview" element={<EmailTemplatePreview />} />
                          <Route path="customers" element={<CustomerManagement />} />
                          <Route path="shipping" element={<ShippingManagement />} />
                          <Route path="homepage" element={<HomepageEditor />} />
                          <Route path="navigation" element={<NavigationEditor />} />
                          <Route path="announcement" element={<AnnouncementEditor />} />
                          <Route path="accessories" element={<AccessoryManagement />} />
                          <Route path="categories/new" element={<CategoryForm />} />
                          <Route path="categories/edit/:id" element={<CategoryForm />} />
                          <Route path="settings" element={<Settings />} />
                        </Route>
                      </Routes>
                    </div>
                    </ErrorBoundary>
                  </ThemeProvider>
                </AdminRoute>
              } />

              {/* Main App Routes - With Header/Footer */}
              <Route path="*" element={<MainAppLayout />} />
            </Routes>
          </Suspense>
        </PerformanceOptimizer>
          </LocationAwareErrorBoundary>
            </Router>
          </NotificationProvider>
        </CartProvider>
      </AuthProvider>
    </LanguageProvider>
    </CookieConsentProvider>
    </HelmetProvider>
  );
}

export default App;
